import { datadogRum, type RumEvent } from '@datadog/browser-rum';
import { reactPlugin } from '@datadog/browser-rum-react';
import { ENV, isDev } from '../config/env';
import { BITBUCKET_COMMIT } from '../config/appVersion';

/**
 * Sends handled exceptions to Datadog RUM / Error Tracking when RUM is initialized
 * (staging and production). Skips in local development where RUM is not started.
 * Prefer this over console.error to satisfy static analysis and still get observability.
 */
export function captureHandledError(
  message: string,
  error: unknown,
  context?: Record<string, unknown>
): void {
  if (isDev()) {
    return;
  }
  try {
    datadogRum.addError(error, { handled_message: message, ...context });
  } catch {
    void 0;
  }
}

// Initialize Datadog RUM
export const initializeDatadogRUM = (): void => {
  if (isDev()) {
    return;
  }

  const config = {
    applicationId: 'dde35f59-242c-4bab-bb5e-4d58917e3228',
    clientToken: 'pube8c9eff3ae6816e2fc16097e7ea0d695',
    site: 'us5.datadoghq.com',
    service: 'marketsmart-frontend',
    env: ENV.toLowerCase(),
    // Match datadog-ci sourcemaps --release-version ($BITBUCKET_COMMIT)
    version: BITBUCKET_COMMIT,
    sessionSampleRate: 100, // 100% of sessions
    sessionReplaySampleRate: 20, // 20% of sessions for session replay (matching example)
    defaultPrivacyLevel: 'mask-user-input' as const, // Mask user input for privacy
    trackingConsent: 'granted' as const, // Required: SDK won't send data without consent
    plugins: [reactPlugin({ router: true })], // React Router integration
    beforeSend: (event: RumEvent) => {
      // Filter out expected 401 Unauthorized errors from /api/auth/me
      // These are normal when checking authentication status on initial load
      if (event.type === 'error' && event.error) {
        const error = event.error;
        const errorMessage = error.message || '';
        const errorSource = error.source || '';
        const resourceUrl =
          'resource' in event &&
          event.resource != null &&
          typeof event.resource === 'object' &&
          'url' in event.resource
            ? String((event.resource as { url?: string }).url || '')
            : '';
        
        // Filter out 401 Unauthorized errors from /api/auth/me endpoint
        if (
          (errorMessage.includes('Unauthorized') || errorMessage.includes('401')) &&
          (resourceUrl.includes('/api/auth/me') || errorSource.includes('/api/auth/me'))
        ) {
          return false; // Drop this event
        }
      }
      
      // Filter out 401 errors from resources (network requests)
      if (event.type === 'resource' && event.resource) {
        const status = event.resource.status_code;
        const url = event.resource.url || '';
        
        // Filter out 401 errors from /api/auth/me endpoint
        if (status === 401 && url.includes('/api/auth/me')) {
          return false; // Drop this event
        }
      }
      
      // Return true to keep the event, false to drop it
      return true;
    },
  };

  // Validate required credentials
  if (!config.applicationId || !config.clientToken) {
    return;
  }

  try {
    datadogRum.init(config);

    // Wait for SDK to be ready, then verify initialization
    datadogRum.onReady(() => {
      try {
        // Access session info through the global DD_RUM object
        const ddRum = window.DD_RUM;
        if (ddRum) {
          // Check if trackViewsManually is enabled - if so, we need to start tracking manually
          const config = ddRum.getInitConfiguration?.();
          const trackViewsManually = Boolean(config?.['trackViewsManually']);
          
          if (trackViewsManually) {
            // Manually start tracking the initial view
            const currentPath = window.location.pathname || '/';
            datadogRum.startView(currentPath);
      
          }
          
          // Manually trigger a test action to verify events are being sent
          datadogRum.addAction('rum_initialized', {
            timestamp: Date.now(),
            environment: ENV.toLowerCase(),
            version: BITBUCKET_COMMIT,
            test: true,
          });
        } else {
          datadogRum.addAction('rum_dd_rum_missing', {
            path: window.location.pathname || '/',
          });
        }
      } catch (e) {
        try {
          datadogRum.addError(e, { source: 'datadog_onready' });
        } catch {
          void 0;
        }
      }
    });

    // Also add a test action after a short delay to ensure SDK is fully ready
    setTimeout(() => {
      try {
        datadogRum.addAction('startup_test', {
          timestamp: Date.now(),
          environment: ENV.toLowerCase(),
          version: BITBUCKET_COMMIT,
          test: true,
        });
      } catch (e) {
        try {
          datadogRum.addError(e, { source: 'datadog_startup_test' });
        } catch {
          void 0;
        }
      }
    }, 2000);

  } catch {
    void 0;
  }
};

// Helper function to set user context (call this after user authentication)
export const setDatadogUser = (userId: string, userName?: string, userEmail?: string): void => {
  try {
    datadogRum.setUser({
      id: userId,
      ...(userName && { name: userName }),
      ...(userEmail && { email: userEmail }),
    });
  } catch (error) {
    captureHandledError('Failed to set Datadog user', error);
  }
};

// Helper function to clear user context (call this on logout)
export const clearDatadogUser = (): void => {
  try {
    datadogRum.clearUser();
  } catch (error) {
    captureHandledError('Failed to clear Datadog user', error);
  }
};

// Helper function to add custom action
export const addDatadogAction = (name: string, context?: Record<string, unknown>): void => {
  try {
    datadogRum.addAction(name, context);
  } catch (error) {
    captureHandledError('Failed to add Datadog action', error);
  }
};

// Helper function to update view name when page changes
// Call this whenever the active page changes (e.g., from AuthContext activePage)
export const updateDatadogView = (viewName: string): void => {
  try {
    // if (isDev()) {
    //   return; // Don't track in development
    // }
    
    if (!viewName) {
      return; // Don't update if no view name provided
    }
    
    const ddRum = window.DD_RUM;
    if (ddRum) {
      const config = ddRum.getInitConfiguration?.();
      const trackViewsManually = Boolean(config?.['trackViewsManually']);
      
      if (trackViewsManually) {
        // Use startView to create a new view with the page name
        datadogRum.startView(viewName);
      } else {
        // If not manual, use setViewName to update the current view
        ddRum.setViewName?.(viewName);
      }
    }
  } catch (error) {
    captureHandledError('Failed to update Datadog view', error);
  }
};

